import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';

import { isSignPage, isPublicPage } from './url';

const COOKIES_NAMES = ['session_id', 'token', 'sessionId', 'tokenId'];

const getCookieDomains = () => {
  const configuredDomains = (process.env.REACT_APP_DOMAINS_URL || '')
    .split(',')
    .map(domain => domain.trim())
    .filter(Boolean);
  const currentHostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const domains = [...configuredDomains, currentHostname].filter(Boolean);

  return [...new Set(domains.flatMap(domain => [domain, `.${domain}`]))];
};

export const deleteCookieVariants = name => {
  Cookies.remove(name, { path: '/' });

  getCookieDomains().forEach(domain => {
    Cookies.remove(name, { path: '/', domain });
  });
};

export const setAuthTokenCookie = token => {
  const parts = token.split(',');
  const decoded = jwtDecode(parts[parts.length - 1]);
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const configuredFirst = (process.env.REACT_APP_DOMAINS_URL || '').split(',')[0].trim();
  const domain = configuredFirst || hostname;
  const options = {
    expires: new Date(decoded.exp * 1000),
    path: '/',
    // Track the page's own protocol: an unconditional `secure: true` makes the
    // browser silently drop the cookie on http://127.0.0.1, which is exactly how
    // local dev runs — login then fails with no visible error.
    secure: window.location.protocol === 'https:',
    sameSite: 'Lax'
  };

  if (domain) {
    options.domain = domain;
  }

  deleteCookieVariants('token');
  Cookies.set('token', token, options);

  // eslint-disable-next-line no-console
  console.warn('[auth] setAuthTokenCookie domain=%s configuredFirst=%s hostname=%s domainUsed=%s', domain, configuredFirst, hostname, options.domain || '(none)');
};

export const deleteAuthCookies = () => {
  COOKIES_NAMES.forEach(name => {
    deleteCookieVariants(name);
  });
};

/**
 * Should the App bootstrap try a silent `prompt=none` warm login on this load?
 *
 * Every Mayar login already opens an `idp_session` at auth-v2, so a user who
 * signed in at wallet/kyc/account is warm at the IdP and `/authorize?prompt=none`
 * can mint www a token with no interaction. www had no /authorize client at all,
 * which is the whole of bug #2 — the session was always there, nobody asked.
 *
 * Probe ONLY where the bootstrap would otherwise have bounced an anonymous
 * visitor to /sign-in, so every path that already works stays untouched:
 *   - a `token` cookie     — already signed in; nothing to warm up.
 *   - a `#token=` fragment — a login is mid-handoff and is about to deliver the
 *     pair (both the legacy /oauth2/google redirect and the OIDC callback land
 *     this way). Probing would race the token that is already arriving.
 *   - a public page        — renders for anonymous visitors by design.
 *   - a sign page          — /sign-in owns its own probe and its ?sso=none
 *     loop-breaker (pages/AuthSignIn/signin.jsx). Probing here too would
 *     double-spend MAX_SILENT_ATTEMPTS against a single page load. This also
 *     keeps /sign-in/{referral,campaign,sub}/:id on the legacy flow, which is
 *     required rather than incidental: their affiliate context rides the legacy
 *     redirect and auth-v2's /authorize drops it.
 *
 * @return {boolean} start the probe.
 */
export const shouldStartWarmLogin = () => {
  if (Cookies.get('token')) {
    // eslint-disable-next-line no-console
    console.warn('[auth] shouldStartWarmLogin: FALSE (token cookie exists)');
    return false;
  }
  if (new URLSearchParams(window.location.hash.slice(1)).get('token')) {
    // eslint-disable-next-line no-console
    console.warn('[auth] shouldStartWarmLogin: FALSE (hash token exists)');
    return false;
  }
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isKnownDomain = hostname.includes('mayar.club') || hostname.includes('mayar.id') || hostname.includes('mayar.io') || hostname.includes('mayar.so');
  // eslint-disable-next-line no-console
  console.warn('[auth] shouldStartWarmLogin hostname=%s isKnown=%s', hostname, isKnownDomain);
  return isKnownDomain && !isSignPage() && !isPublicPage();
};
